/**
 * Utility functions for downloading reports in various formats.
 */

import { toJpeg } from 'html-to-image';
import { jsPDF } from 'jspdf';

/** Wrap a promise with a timeout — rejects if the promise doesn't settle in `ms` */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
    return Promise.race([
        promise,
        new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
        ),
    ]);
}

export interface ReportData {
    dataset: {
        name: string;
        rowCount: number;
        columnCount: number;
    };
    healthScore: {
        overall: number;
        dimensions: Record<string, number>;
    };
    summary?: {
        headline: string;
        keyFindings: string[];
        nextSteps?: string[];
        recommendedNextAction?: string;
    };
    problems: any[];
    executiveSummary?: Record<string, string>;
}

/**
 * Export report as a single tall-page PDF using html-to-image (JPEG).
 *
 * Strategy:
 * 1. Show opaque overlay so user doesn't see DOM changes
 * 2. Inject <style> with !important rules — these survive React re-renders
 *    (React sets inline styles WITHOUT !important, stylesheet !important wins)
 * 3. Scroll through sections to trigger IntersectionObserver animations
 * 4. Programmatically click expand buttons (React conditional rendering)
 * 5. Capture each section at narrower width (1100px) for readable zoom
 * 6. Stitch into single tall-page PDF
 * 7. Collapse buttons, remove style tag + overlay, restore scroll
 */
export async function downloadReportAsPDF(datasetName: string): Promise<boolean> {
    // ── Clean up any stuck overlays from previous failed/interrupted attempts ──
    document.querySelectorAll('[data-pdf-overlay]').forEach(el => el.remove());
    document.getElementById('pdf-capture-override')?.remove();

    const sectionIds = ['hero', 'dimensions', 'problems'];
    const sections = sectionIds
        .map(id => document.getElementById(id))
        .filter((el): el is HTMLElement => el !== null);

    if (sections.length === 0) {
        console.error('No report sections found');
        return false;
    }

    console.log('[PDF] Found sections:', sections.map(s => `${s.id} (${s.scrollWidth}x${s.scrollHeight})`));

    const prevScroll = window.scrollY;

    // ── Show fully-opaque overlay to hide all DOM changes from user ──
    const overlay = document.createElement('div');
    overlay.dataset.pdfOverlay = 'true'; // mark so stuck-overlay cleanup can find it
    overlay.style.cssText = `
        position: fixed; inset: 0; z-index: 99999;
        background: #ffffff;
        display: flex; align-items: center; justify-content: center;
        font-family: 'Alright Sans', system-ui, -apple-system, sans-serif;
    `;
    overlay.innerHTML = `
        <div style="text-align:center;display:flex;flex-direction:column;align-items:center;gap:24px" id="pdf-overlay-content">
            <div style="width:48px;height:48px;border:4px solid #f3f4f6;border-top-color:#C8102E;border-radius:50%;animation:pdf-spin 0.8s linear infinite"></div>
            <div>
                <div style="font-size:22px;font-weight:600;color:#C8102E;margin-bottom:8px">Generating PDF</div>
                <div id="pdf-overlay-status" style="font-size:14px;color:#6b7280">This may take a moment, please wait...</div>
            </div>
        </div>
        <style>@keyframes pdf-spin{to{transform:rotate(360deg)}}</style>
    `;
    document.body.appendChild(overlay);

    // Helper to update overlay status text
    const updateStatus = (text: string) => {
        const el = document.getElementById('pdf-overlay-status');
        if (el) el.textContent = text;
    };

    // ── Safety timeout: remove overlay after 10 minutes no matter what ──
    const overlaySafetyTimer = setTimeout(() => {
        document.querySelectorAll('[data-pdf-overlay]').forEach(el => el.remove());
        document.getElementById('pdf-capture-override')?.remove();
    }, 600000);

    await new Promise(r => setTimeout(r, 100));

    // ── Inject stylesheet — !important in stylesheet ALWAYS beats React inline styles ──
    // React does el.style.opacity = '0' (inline, no !important) → our stylesheet wins.
    const captureStyle = document.createElement('style');
    captureStyle.id = 'pdf-capture-override';
    captureStyle.textContent = `
        /* ── Normalize ALL sections to same width / padding / no gaps ── */
        #hero, #dimensions, #problems {
            width: 1100px !important;
            max-width: 1100px !important;
            min-height: auto !important;
            height: auto !important;
            overflow: visible !important;
            display: block !important;
            padding: 32px 40px !important;
            box-sizing: border-box !important;
            background: #ffffff !important;
        }

        /* Force all animated/hidden content visible */
        #hero *, #dimensions *, #problems * {
            opacity: 1 !important;
            visibility: visible !important;
            transition: none !important;
            animation: none !important;
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
        }

        /* Make child divs fill the section width */
        #hero > div,
        #dimensions > div,
        #problems > div {
            max-width: 100% !important;
            width: 100% !important;
        }

        /* Preserve natural grid layout — matches live report (Diagnosis / Prescription side-by-side) */
        #problems [class*="grid-cols"] {
            display: grid !important;
        }

        /* Normalize inner content wrappers (max-w-6xl, max-w-7xl, etc) */
        #hero [class*="max-w-"],
        #dimensions [class*="max-w-"],
        #problems [class*="max-w-"] {
            max-width: 100% !important;
            width: 100% !important;
        }

        /* Preserve SVG dimensions — prevent width:100% from stretching the ring */
        #hero svg, #dimensions svg, #problems svg {
            width: auto !important;
            max-width: none !important;
        }

        /* Kill min-height constraints on dimension cards */
        #dimensions [class*="min-h-"] {
            min-height: auto !important;
        }

        /* Reduce oversized card padding in dimensions */
        #dimensions [class*="backdrop-blur"] {
            padding: 32px !important;
        }

        /* ── User Prescribed Rules PDF styling ── */
        #dimensions .bg-red-50 {
            padding: 16px !important;
            margin-bottom: 12px !important;
            page-break-inside: avoid !important;
        }
        #dimensions table {
            font-size: 12px !important;
            width: 100% !important;
            table-layout: fixed !important;
            page-break-inside: avoid !important;
        }
        #dimensions table th,
        #dimensions table td {
            padding: 8px 6px !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
        }

        /* ── Problems section — ZERO side padding so content fills full page width ── */
        #problems {
            background: #ffffff !important;
            min-width: 1100px !important;
            padding: 32px 0 !important;
        }
        /* Kill decorative gradient orbs that render as black */
        #problems > div.absolute,
        #problems [class*="absolute"][class*="inset-0"] {
            display: none !important;
        }
        /* Ensure problems content wrapper stays on top and fills full width — kill mx-auto margin */
        #problems > div.relative {
            position: relative !important;
            z-index: 1 !important;
            width: 100% !important;
            max-width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
        }
        /* Kill the huge mb-20 on header wrapper */
        #problems .mb-20 {
            margin-bottom: 16px !important;
        }
        /* Kill mb-12 on heading container */
        #problems .mb-12 {
            margin-bottom: 8px !important;
        }
        /* Tighten card spacing */
        #problems .space-y-6 > * + * {
            margin-top: 12px !important;
        }
        /* Kill backdrop-blur backgrounds that render black */
        #problems .backdrop-blur-sm,
        #problems .backdrop-blur-md,
        #problems .backdrop-blur-lg,
        #problems .backdrop-blur-xl {
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
            background: #ffffff !important;
        }
        /* Force white on problem card containers */
        #problems [class*="rounded-3xl"],
        #problems [class*="rounded-2xl"],
        #problems [class*="rounded-xl"] {
            background: #ffffff !important;
            box-shadow: 0 1px 3px rgba(0,0,0,0.08) !important;
        }
        /* Compact card padding — zero sides so content fills page width */
        #problems .p-8 {
            padding: 24px 0 !important;
        }
        /* Kill side padding on card headers */
        #problems .px-8 {
            padding-left: 0 !important;
            padding-right: 0 !important;
        }
        /* Tighten internal card spacing */
        #problems .space-y-4 > * + * {
            margin-top: 8px !important;
        }
        #problems .mb-6 {
            margin-bottom: 8px !important;
        }
        #problems .mb-8 {
            margin-bottom: 10px !important;
        }
        #problems .py-4 {
            padding-top: 8px !important;
            padding-bottom: 8px !important;
        }

        /* Hide dropdown chevron SVGs in dimensions */
        #dimensions [aria-expanded] svg {
            display: none !important;
        }

        /* Hide floating UI buttons */
        .report-navigation-buttons,
        .fixed.bottom-6,
        .fixed.top-24 {
            display: none !important;
        }
    `;
    document.head.appendChild(captureStyle);
    await new Promise(r => setTimeout(r, 500)); // let full reflow settle

    // Track buttons we clicked so we can un-click them later
    const clickedButtons: HTMLElement[] = [];

    // Track SVG attribute changes for cleanup
    const savedSvgAttrs: { el: SVGTextElement; attr: string; val: string | null }[] = [];

    try {
        // ── Phase 1: Scroll through sections to trigger IntersectionObserver ──
        for (const section of sections) {
            section.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start' });
            await new Promise(r => setTimeout(r, 1500));
        }

        // ── Phase 2: Expand all dropdowns/accordions ──
        // Scroll to each section BEFORE clicking its buttons so React state
        // changes happen while the section is in the viewport.

        // 2a. Hero section — "View More" button for extra summary
        sections[0]?.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start' });
        await new Promise(r => setTimeout(r, 800));
        const heroButtons = document.querySelectorAll('#hero button');
        for (const btn of heroButtons) {
            const htmlBtn = btn as HTMLElement;
            if (htmlBtn.textContent?.trim().includes('View More')) {
                htmlBtn.click();
                clickedButtons.push(htmlBtn);
                await new Promise(r => setTimeout(r, 300));
            }
        }

        // 2b. Dimensions section — keep collapsed (no dropdown expansion)
        // Just scroll to trigger visibility
        sections[1]?.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start' });
        await new Promise(r => setTimeout(r, 1000));

        // 2c. Problems section — expand all problem cards
        sections[2]?.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start' });
        await new Promise(r => setTimeout(r, 1000));
        const problemCards = document.querySelectorAll('#problems [aria-expanded="false"]');
        for (const card of problemCards) {
            const htmlCard = card as HTMLElement;
            htmlCard.click();
            clickedButtons.push(htmlCard);
            await new Promise(r => setTimeout(r, 200));
        }

        // 2d. "See More" buttons inside expanded problem cards (action steps)
        await new Promise(r => setTimeout(r, 500)); // wait for cards to render
        const seeMoreBtns = document.querySelectorAll('#problems button');
        for (const btn of seeMoreBtns) {
            const htmlBtn = btn as HTMLElement;
            if (htmlBtn.textContent?.trim().includes('See More')) {
                htmlBtn.click();
                clickedButtons.push(htmlBtn);
                await new Promise(r => setTimeout(r, 100));
            }
        }

        // Let all expansions settle
        await new Promise(r => setTimeout(r, 600));

        // ── Phase 2.5: Fix SVG ring/text alignment for PDF capture ──
        // The SVG has width/height attributes (280) but NO viewBox.
        // When the container resizes, pixel coords (cx=140) and percentage
        // coords (x="50%") diverge. Adding viewBox locks them together.
        // Also convert percentage text coords to absolute pixels.
        const svgEls = document.querySelectorAll('#dimensions svg[role="img"]');
        svgEls.forEach(svg => {
            const svgEl = svg as SVGSVGElement;
            const w = svgEl.getAttribute('width');
            const h = svgEl.getAttribute('height');
            if (w && h && !svgEl.getAttribute('viewBox')) {
                savedSvgAttrs.push({ el: svgEl as unknown as SVGTextElement, attr: 'viewBox', val: null });
                svgEl.setAttribute('viewBox', `0 0 ${w} ${h}`);
            }
            // Convert text x/y from "50%" to absolute pixel center
            const centerX = w ? String(Number(w) / 2) : '140';
            const centerY = h ? String(Number(h) / 2) : '140';
            svgEl.querySelectorAll('text').forEach(textEl => {
                const tx = textEl.getAttribute('x');
                const ty = textEl.getAttribute('y');
                if (tx === '50%') {
                    savedSvgAttrs.push({ el: textEl, attr: 'x', val: tx });
                    textEl.setAttribute('x', centerX);
                }
                if (ty === '50%') {
                    savedSvgAttrs.push({ el: textEl, attr: 'y', val: ty });
                    textEl.setAttribute('y', centerY);
                }
            });
        });

        // ── Phase 3: Capture each section while scrolled to it ──
        const images: HTMLImageElement[] = [];

        // Shared filter for html-to-image captures
        const captureFilter = (node: HTMLElement) => {
            if (node.tagName === 'BUTTON') {
                const text = node.textContent?.trim() || '';
                if (text.includes('See More') || text.includes('See Less') ||
                    text.includes('View More') || text.includes('View Less')) {
                    return false;
                }
            }
            if (!node.className || typeof node.className !== 'string') return true;
            const classes = node.className.split(/\s+/);
            if (classes.includes('blur-3xl') || classes.includes('blur-2xl')) return false;
            if (classes.includes('absolute') && classes.includes('inset-0')) return false;
            if (classes.includes('pointer-events-none') && classes.includes('absolute')) return false;
            return true;
        };

        for (const section of sections) {
            section.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start' });
            await new Promise(r => setTimeout(r, 1000));

            // ── PROBLEMS SECTION: Chunked capture via clone + clipper ──
            // The problems section can be extremely tall (70,000+ px when all cards
            // are expanded). Browsers cap canvas dimensions at ~16384px, so we
            // capture in vertical chunks of max 7000 CSS px (= 14000 canvas px at
            // pixelRatio:2, safely under the browser ceiling).
            if (section.id === 'problems') {
                const cloneEl = section.cloneNode(true) as HTMLElement;

                // Position clone inside a clipper wrapper (absolute, not fixed)
                cloneEl.style.cssText = [
                    'position:absolute',
                    'top:0',
                    'left:0',
                    'width:1100px',
                    'max-width:1100px',
                    'min-height:auto',
                    'height:auto',
                    'overflow:visible',
                    'box-sizing:border-box',
                    'background:#ffffff',
                    'opacity:1',
                    'visibility:visible',
                ].join(';');
                cloneEl.style.setProperty('padding', '32px 0', 'important');

                // Remove decorative gradient orb containers
                Array.from(cloneEl.querySelectorAll(':scope > div')).forEach(div => {
                    const el = div as HTMLElement;
                    const classes = el.className?.split?.(/\s+/) || [];
                    if (classes.includes('absolute') && classes.includes('inset-0')) el.remove();
                });

                // Force content wrapper full width — kill mx-auto margin
                const cw = cloneEl.querySelector(':scope > div.relative') as HTMLElement | null;
                if (cw) {
                    cw.style.setProperty('width', '100%', 'important');
                    cw.style.setProperty('max-width', '100%', 'important');
                    cw.style.setProperty('margin', '0', 'important');
                    cw.style.setProperty('padding', '0', 'important');
                }

                // Remove all max-width constraints
                cloneEl.querySelectorAll('[class*="max-w-"]').forEach(el => {
                    (el as HTMLElement).style.setProperty('max-width', '100%', 'important');
                    (el as HTMLElement).style.setProperty('width', '100%', 'important');
                });

                // Force visibility, kill animations
                cloneEl.querySelectorAll<HTMLElement>('*').forEach(el => {
                    el.style.setProperty('opacity', '1', 'important');
                    el.style.setProperty('visibility', 'visible', 'important');
                    el.style.setProperty('transition', 'none', 'important');
                    el.style.setProperty('animation', 'none', 'important');
                    el.style.setProperty('backdrop-filter', 'none', 'important');
                    el.style.setProperty('-webkit-backdrop-filter', 'none', 'important');
                    if (el.tagName === 'DIV' && el.style.willChange?.includes('transform')) {
                        el.style.setProperty('transform', 'none', 'important');
                    }
                });

                // Force 2-column grid
                cloneEl.querySelectorAll('[class*="lg:grid-cols-2"]').forEach(el => {
                    (el as HTMLElement).style.setProperty('grid-template-columns', 'repeat(2, minmax(0, 1fr))', 'important');
                });

                // Force white backgrounds
                cloneEl.querySelectorAll('div').forEach(div => {
                    const el = div as HTMLElement;
                    const computed = window.getComputedStyle(el);
                    const bg = computed.backgroundColor;
                    const bgImg = computed.backgroundImage;
                    if (!bg || bg === 'transparent' || bg === 'rgba(0, 0, 0, 0)' || bg.includes('0)') || bg.includes('0.')) {
                        el.style.setProperty('background-color', '#ffffff', 'important');
                    }
                    if (bgImg && bgImg !== 'none') {
                        el.style.setProperty('background-image', 'none', 'important');
                    }
                });

                // Create clipper wrapper for chunked capture
                const clipper = document.createElement('div');
                clipper.style.cssText = 'position:fixed;top:0;left:0;z-index:99998;width:1100px;overflow:hidden;background:#ffffff;';
                clipper.appendChild(cloneEl);
                document.body.appendChild(clipper);
                await new Promise(r => setTimeout(r, 300));

                const CHUNK_MAX = 7000; // CSS px — safe for pixelRatio:2 (14000 < 16384 canvas limit)
                const totalH = cloneEl.scrollHeight;
                const totalChunks = Math.ceil(totalH / CHUNK_MAX);
                console.log(`[PDF] problems: totalH=${totalH}, chunks=${totalChunks}`);

                if (totalChunks > 3) {
                    updateStatus('This is a large report and may take several minutes. Please wait...');
                }

                try {
                    let chunkIdx = 0;
                    for (let offset = 0; offset < totalH; offset += CHUNK_MAX) {
                        chunkIdx++;
                        const chunkH = Math.min(CHUNK_MAX, totalH - offset);
                        clipper.style.height = `${chunkH}px`;
                        cloneEl.style.setProperty('top', `${-offset}px`, 'important');
                        await new Promise(r => requestAnimationFrame(() => setTimeout(r, 50)));

                        const dataUrl = await withTimeout(toJpeg(clipper, {
                            quality: 0.9,
                            pixelRatio: 2,
                            backgroundColor: '#ffffff',
                            fontEmbedCSS: '',
                            filter: captureFilter,
                        }), 90000, `problems chunk at ${offset}`);

                        const img = new Image();
                        await new Promise<void>((resolve, reject) => {
                            img.onload = () => resolve();
                            img.onerror = () => reject(new Error('Failed to load chunk image'));
                            img.src = dataUrl;
                        });
                        images.push(img);
                        console.log(`[PDF] problems chunk@${offset}: ${img.width}x${img.height}`);
                    }
                } finally {
                    try { document.body.removeChild(clipper); } catch (_) { }
                }
                continue; // skip normal capture below
            }

            // ── HERO / DIMENSIONS: Live capture ──
            const bgSaved: { el: HTMLElement; bg: string }[] = [];
            section.querySelectorAll('div').forEach(div => {
                const el = div as HTMLElement;
                const computed = window.getComputedStyle(el);
                const bg = computed.backgroundColor;
                const bgImg = computed.backgroundImage;
                if (!bg || bg === 'transparent' || bg === 'rgba(0, 0, 0, 0)' || bg.includes('0)') || bg.includes('0.')) {
                    bgSaved.push({ el, bg: el.style.backgroundColor });
                    el.style.setProperty('background-color', '#ffffff', 'important');
                }
                if (bgImg && bgImg !== 'none') {
                    bgSaved.push({ el, bg: el.style.backgroundImage });
                    el.style.setProperty('background-image', 'none', 'important');
                }
            });

            await new Promise(r => requestAnimationFrame(() => setTimeout(r, 50)));

            console.log(`[PDF] Capturing ${section.id}: scrollW=${section.scrollWidth}, scrollH=${section.scrollHeight}`);

            const dataUrl = await withTimeout(toJpeg(section, {
                quality: 0.9,
                pixelRatio: 2,
                backgroundColor: '#ffffff',
                fontEmbedCSS: '',
                filter: captureFilter,
            }), 90000, `Capture ${section.id}`);

            for (const { el, bg } of bgSaved) {
                if (bg) {
                    el.style.backgroundColor = bg;
                } else {
                    el.style.removeProperty('background-color');
                }
                el.style.removeProperty('background-image');
            }

            const img = new Image();
            await new Promise<void>((resolve, reject) => {
                img.onload = () => resolve();
                img.onerror = () => reject(new Error('Failed to load section image'));
                img.src = dataUrl;
            });
            images.push(img);
            console.log(`[PDF] ${section.id}: ${img.width}x${img.height}`);
        }

        // ── Phase 4+5: Build PDF directly from images ──
        // Skip canvas stitching entirely — avoids browser canvas size limits
        // (16384px max). Place each image directly into jsPDF.
        const pdfW = 210;
        const margin = 3;
        const usableW = pdfW - margin * 2;

        // Calculate mm height for each image
        const placements = images.map(img => ({
            src: img.src,
            hMm: usableW * (img.height / img.width),
        }));

        const totalHMm = placements.reduce((sum, p) => sum + p.hMm, 0) + margin * 2;
        const MAX_PAGE_H = 14400; // jsPDF limit
        const firstPageH = Math.min(totalHMm, MAX_PAGE_H);

        console.log(`[PDF] Building PDF: ${placements.length} images, totalH=${totalHMm.toFixed(0)}mm, pages=${totalHMm <= MAX_PAGE_H ? 1 : 'multi'}`);

        const pdf = new jsPDF({
            orientation: 'p',
            unit: 'mm',
            format: [pdfW, firstPageH],
        });

        let yMm = margin;
        let currentPageH = firstPageH;

        for (let i = 0; i < placements.length; i++) {
            const p = placements[i];
            // If this image overflows the current page, start a new page
            if (yMm + p.hMm > currentPageH - margin && yMm > margin + 1) {
                const remainH = placements.slice(i).reduce((s, r) => s + r.hMm, 0) + margin * 2;
                currentPageH = Math.min(remainH, MAX_PAGE_H);
                pdf.addPage([pdfW, currentPageH]);
                yMm = margin;
            }
            pdf.addImage(p.src, 'JPEG', margin, yMm, usableW, p.hMm);
            yMm += p.hMm;
        }

        const safeName = sanitizeFilename(datasetName) || 'report';
        pdf.save(`${safeName}_data_quality_report.pdf`);
        return true;
    } catch (err) {
        console.error('PDF export failed:', err);
        return false;
    } finally {
        // ── Collapse expanded sections FIRST while overlay still hides the DOM ──
        // This prevents the user from seeing expanded/modified report behind the overlay.
        clearTimeout(overlaySafetyTimer);

        for (const btn of [...clickedButtons].reverse()) {
            try { btn.click(); } catch (_) { /* ignore stale-ref errors */ }
            await new Promise(r => setTimeout(r, 50));
        }

        // Restore SVG text attributes
        for (const { el, attr, val } of savedSvgAttrs) {
            try {
                if (val !== null) el.setAttribute(attr, val);
                else el.removeAttribute(attr);
            } catch (_) { /* ignore detached-node errors */ }
        }

        // ── Remove stylesheet THEN overlay — report is back to normal before user sees it ──
        try { captureStyle.remove(); } catch (_) { }
        window.scrollTo(0, prevScroll);
        // Small delay so the browser paints the restored DOM before overlay disappears
        await new Promise(r => setTimeout(r, 100));
        try { overlay.remove(); } catch (_) { }
    }
}

/**
 * Download report data as JSON file
 */
export function downloadReportAsJSON(reportData: ReportData, datasetName: string) {
    const jsonData = JSON.stringify(reportData, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${sanitizeFilename(datasetName)}_data_quality_report.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
}

/**
 * Download executive summary as text file
 */
export function downloadExecutiveSummaryAsText(
    summary: ReportData['summary'],
    executiveSummary?: ReportData['executiveSummary'],
    datasetName: string = 'dataset'
) {
    let content = `DATA QUALITY EXECUTIVE SUMMARY\n`;
    content += `${'='.repeat(35)}\n\n`;
    content += `Dataset: ${datasetName}\n`;
    content += `Generated: ${new Date().toLocaleDateString()}\n\n`;

    if (summary) {
        if (summary.headline) {
            content += `HEADLINE\n`;
            content += `--------\n`;
            content += `${summary.headline}\n\n`;
        }

        if (summary.keyFindings && summary.keyFindings.length > 0) {
            content += `KEY FINDINGS\n`;
            content += `------------\n`;
            summary.keyFindings.forEach((finding, index) => {
                content += `${index + 1}. ${finding}\n`;
            });
            content += `\n`;
        }

        if (summary.nextSteps && summary.nextSteps.length > 0) {
            content += `NEXT STEPS\n`;
            content += `----------\n`;
            summary.nextSteps.forEach((step, index) => {
                content += `${index + 1}. ${step}\n`;
            });
            content += `\n`;
        }

        if (summary.recommendedNextAction) {
            content += `RECOMMENDED ACTION\n`;
            content += `------------------\n`;
            content += `${summary.recommendedNextAction}\n\n`;
        }
    }

    if (executiveSummary) {
        content += `DETAILED ANALYSIS\n`;
        content += `=================\n\n`;
        Object.entries(executiveSummary).forEach(([key, value]) => {
            content += `${key}:\n${value}\n\n`;
        });
    }

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${sanitizeFilename(datasetName)}_executive_summary.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
}

/**
 * Sanitize filename for safe download
 */
function sanitizeFilename(filename: string): string {
    return filename
        .replace(/[^a-zA-Z0-9\-_\.]/g, '_')
        .replace(/_{2,}/g, '_')
        .replace(/^_|_$/g, '');
}